import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { LogoFull } from "@/components/LogoFull"
import { usePageTitle } from "@/contexts/PageTitleContext.tsx"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button.tsx"
import {
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Field,
  FieldError,
} from "@/components/ui/field.tsx"
import { Input } from "@/components/ui/input.tsx"
import { CheckCircle, CheckIcon } from "lucide-react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { listsApi } from "@/api/lists.api.ts"
import { LanguageSelector } from "@/components/LanguageSelector.tsx"
import { Badge } from "@/components/ui/badge.tsx"
import { Separator } from "@/components/ui/separator.tsx"
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxValue,
} from "@/components/ui/combobox.tsx"
import {
  cn,
  countryCodeToFlag,
  getStoredLocale,
  handleNumberKeyDown,
  handleNumberPaste,
  uniqueById,
} from "@/lib/utils.ts"
import { userApi } from "@/api/user.api.ts"
import { useTranslation } from "react-i18next"
import { InputGroup, InputGroupAddon } from "@/components/ui/input-group.tsx"

export const Route = createFileRoute("/_unauthorized/signup")({
  ssr: false,
  component: Signup,
})

// export enum Gender {
//   Man = 1,
//   Woman = 2,
//   PreferNotToSay = 3,
// }

const genders = [
  { id: 1, name: "Man" },
  { id: 2, name: "Woman" },
  { id: 3, name: "Prefer not to say" },
]

function Signup() {
  const { setTitle } = usePageTitle()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const locale = getStoredLocale()

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [completeError, setCompleteError] = useState<string>("")
  const [email, setEmail] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [repeatPassword, setRepeatPassword] = useState<string>("")
  const [step, setStep] = useState(1)
  const [emailError, setEmailError] = useState("")
  const [firstName, setFirstName] = useState("")
  const [firstNameError, setFirstNameError] = useState(false)
  const [lastName, setLastName] = useState("")
  const [phone, setPhone] = useState("")
  const [city, setCity] = useState("")
  const [phoneError, setPhoneError] = useState(false)
  const [cityError, setCityError] = useState(false)
  const [gender, setGender] = useState(null)
  const [country, setCountry] = useState(null)
  const [phoneCode, setPhoneCode] = useState(null)
  const [lastNameError, setLastNameError] = useState(false)
  const [passwordError, setPasswordError] = useState({
    minLength: true,
    hasUppercase: true,
    hasNumber: true,
    hasSpecialChar: true,
  })
  const [repeatError, setRepeatError] = useState(false)
  const [pitchFormat, setPitchFormat] = useState([])
  const [pitchThemes, setPitchThemes] = useState([])
  const { data } = useQuery({
    queryKey: ["getSignupOptions"],
    queryFn: () => listsApi.getSignupOptions(),
    staleTime: 60 * 60 * 1000,
    gcTime: 120 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  const registerAccount = useMutation({
    mutationFn: userApi.register,
  })

  const formats =
    data?.result?.formats.map((format) => ({
      id: format.id,
      name: format.nameKey,
    })) || []

  const themes =
    data?.result?.themes.map((theme) => ({
      id: theme.id,
      name: theme.nameKey,
    })) || []

  const countries =
    data?.result?.countries.map((country) => ({
      id: country.id,
      name: country.name,
    })) || []

  const countryCodes =
    data?.result?.countries.map((country) => ({
      id: country.code,
      name: country.phoneCode,
      flag: countryCodeToFlag(country.code),
    })) || []

  const onChangeEmail = (e) => {
    setEmail(e.target.value)
  }

  const onChangePassword = (e) => {
    setPassword(e.target.value)
    validatePassword()
  }
  const onChangeRepeatPassword = (e) => {
    setRepeatError(false)
    setRepeatPassword(e.target.value)
  }
  const validateFirstStep = () => {
    return !(
      email.trim().length > 0 &&
      password.trim().length > 0 &&
      repeatPassword.trim().length > 0 &&
      emailError === "" &&
      passwordError.hasUppercase &&
      passwordError.hasNumber &&
      passwordError.hasSpecialChar &&
      passwordError.minLength &&
      !repeatError
    )
  }

  const validateSecondStep = () => {
    return !(
      firstName.trim().length > 0 &&
      lastName.trim().length > 0 &&
      gender &&
      phoneCode &&
      phone.trim().length > 5 &&
      country &&
      city.trim().length > 0
    )
  }
  const validateThirdStep = () => {
    return !(pitchThemes.length > 0 && pitchFormat.length > 0)
  }

  const onClickContinue = (e) => {
    e.preventDefault()
    setStep(2)
  }
  const onChangeFirstName = (e) => {
    setFirstName(e.target.value)
    setFirstNameError(false)
  }

  const validateFirstName = () => {
    if (firstName.trim() === "") {
      setFirstNameError(true)
    } else {
      setFirstNameError(false)
    }
  }
  const onChangeLastName = (e) => {
    setLastName(e.target.value)
    setLastNameError(false)
  }

  const onChangePhone = (e) => {
    setPhone(e.target.value)
    setPhoneError(false)
  }

  const onChangeCity = (e) => {
    setCity(e.target.value)
    setCityError(false)
  }

  const validateLastName = () => {
    if (firstName.trim() === "") {
      setLastNameError(true)
    } else {
      setLastNameError(false)
    }
  }
  const validatePhone = () => {
    if (firstName.trim() === "") {
      setPhoneError(true)
    } else {
      setPhoneError(false)
    }
  }
  const validateCity = () => {
    if (firstName.trim() === "") {
      setCityError(true)
    } else {
      setCityError(false)
    }
  }
  const onChangePitchFormats = (types) => {
    setPitchFormat(uniqueById(types))
  }
  const onChangePitchThemes = (topics) => {
    if (topics.length < 6) {
      setPitchThemes(uniqueById(topics))
    }
  }

  const onClickContinueStep2 = (e) => {
    e.preventDefault()
    setStep(3)
  }
  const onClickBackStep2 = (e) => {
    e.preventDefault()
    setStep(1)
  }

  const onClickBackStep3 = (e) => {
    e.preventDefault()
    setIsSubmitting(false)
    setIsError(false)
    setCompleteError("")
    setStep(2)
  }
  const onClickComplete = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    const payload = {
      name: firstName,
      surname: lastName,
      emailAddress: email,
      password: password,
      gender: gender.id,
      whatsAppCountryCode: phoneCode.name,
      whatsAppNumber: phone,
      currentLocationCountryId: country.id,
      currentLocationCity: city,
      pitchFormatIds: pitchFormat.map((item) => item.id),
      pitchThemeIds: pitchThemes.map((item) => item.id),
      locale: getStoredLocale(),
    }

    registerAccount.mutate(payload, {
      onSuccess: () => {
        setIsSubmitting(false)
        setStep(4)
      },
      onError: (error) => {
        setIsError(true)
        setCompleteError(error.message)
        setIsSubmitting(false)
      },
    })
  }

  const validateEmail = (e: any) => {
    if (e.target.value.trim() === "") {
      setEmailError(t("signup.requiredEmail"))
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value.trim())) {
      setEmailError(t("signup.invalidEmail"))
    } else {
      setEmailError("")
    }
  }

  const validatePassword = () => {
    setPasswordError({
      minLength: password.length >= 6,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /['"!@#$*;&:?\-+]/.test(password),
    })
  }

  const validateRepeatPassword = () => {
    if (password !== repeatPassword) {
      setRepeatError(true)
    }
  }

  useEffect(() => {
    setTitle(t("signup.signup"))
  }, [setTitle])

  return (
    <div className="flex h-full w-full max-w-md flex-col items-center p-4 text-center">
      <LogoFull />
      {step === 1 && (
        <div className="w-full not-md:max-w-md">
          <h2
            className={cn(
              "mb-4 text-center font-heading text-xl",
              locale === "ar" && "font-arabic!"
            )}
          >
            {t("signup.title")}
          </h2>
          <div className="flex flex-col gap-6">
            <FieldDescription className="text-center">
              {t("signup.haveAccount")}{" "}
              <Link to={"/login"}> {t("signup.signin")}</Link>
            </FieldDescription>
            <form>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="email">{t("signup.email")}</FieldLabel>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    required
                    className="h-10"
                    value={email}
                    onChange={onChangeEmail}
                    onBlur={validateEmail}
                  />
                  {emailError !== "" && (
                    <FieldError className="text-start">{emailError}</FieldError>
                  )}
                </Field>
                <Field>
                  <div className="flex items-center">
                    <FieldLabel htmlFor="password">
                      {t("signup.password")}
                    </FieldLabel>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder={t("signup.password")}
                    required
                    className="h-10"
                    value={password}
                    onChange={onChangePassword}
                    onBlur={validatePassword}
                  />
                  <>
                    {!passwordError.minLength && (
                      <FieldError className="text-start">
                        {t("signup.minLength")}
                      </FieldError>
                    )}
                    {!passwordError.hasUppercase && (
                      <FieldError className="text-start">
                        {t("signup.upperCase")}
                      </FieldError>
                    )}
                    {!passwordError.hasNumber && (
                      <FieldError className="text-start">
                        {t("signup.number")}
                      </FieldError>
                    )}
                    {!passwordError.hasSpecialChar && (
                      <FieldError className="text-start">
                        {t("signup.special")}
                      </FieldError>
                    )}
                  </>
                </Field>
                <Field>
                  <div className="flex items-center">
                    <FieldLabel htmlFor="passwordRepeat">
                      {t("signup.repeatPassword")}
                    </FieldLabel>
                  </div>
                  <Input
                    id="passwordRepeat"
                    type="password"
                    placeholder={t("signup.repeatPassword")}
                    required
                    className="h-10"
                    value={repeatPassword}
                    onChange={onChangeRepeatPassword}
                    onBlur={validateRepeatPassword}
                  />
                  {repeatError && (
                    <FieldError className="text-start">
                      {t("signup.repeatPasswordError")}
                    </FieldError>
                  )}
                </Field>
                <Field>
                  <Button
                    type="submit"
                    disabled={validateFirstStep()}
                    onClick={onClickContinue}
                    size="lg"
                  >
                    {t("signup.continue")}
                  </Button>
                </Field>
              </FieldGroup>
            </form>
            <FieldDescription className="px-6 text-center">
              {t("signup.agreement")} <a href="#">{t("signup.terms")}</a>{" "}
              {t("signup.and")} <a href="#">{t("signup.privacy")}</a>.
            </FieldDescription>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className="flex w-full flex-col items-center not-md:max-w-md">
          <div className="mb-4 flex w-full items-center justify-between gap-2 pe-6">
            <Badge>
              <CheckIcon />
            </Badge>
            <Separator className="w-1/3!" />
            <Badge>2</Badge>
            <Separator className="w-1/3!" />
            <Badge variant="outline" className="border-primary text-primary">
              3
            </Badge>
          </div>
          <h2
            className={cn(
              "mb-4 text-center font-heading text-xl",
              locale === "ar" && "font-arabic!"
            )}
          >
            {t("signup.title2")}
          </h2>
          <form className="w-full max-w-md">
            <FieldGroup className="mb-4 flex w-full flex-row justify-between">
              <Field className="w-1/2">
                <FieldLabel htmlFor="firstName">
                  {t("signup.firstName")}
                </FieldLabel>
                <Input
                  id="firstName"
                  type="text"
                  placeholder={t("signup.firstName")}
                  required
                  className="h-10"
                  value={firstName}
                  onChange={onChangeFirstName}
                  onBlur={validateFirstName}
                />
                {firstNameError && (
                  <FieldError className="text-start">
                    {t("signup.firstName")} {t("signup.required")}
                  </FieldError>
                )}
              </Field>
              <Field className="w-1/2">
                <FieldLabel htmlFor="lastName">
                  {t("signup.lastName")}
                </FieldLabel>
                <Input
                  id="lastName"
                  type="text"
                  placeholder={t("signup.lastName")}
                  required
                  className="h-10"
                  value={lastName}
                  onChange={onChangeLastName}
                  onBlur={validateLastName}
                />
                {lastNameError && (
                  <FieldError className="text-start">
                    {t("signup.lastName")} {t("signup.required")}
                  </FieldError>
                )}
              </Field>
            </FieldGroup>
            <FieldGroup className="mb-4">
              <FieldLabel>{t("signup.gender")}</FieldLabel>
              <Field>
                <Combobox
                  items={genders}
                  onValueChange={setGender}
                  itemToStringLabel={(item) => item?.name}
                >
                  <ComboboxInput
                    className="h-10"
                    placeholder={`${t("create.select")}${t("signup.gender")}`}
                  />
                  <ComboboxContent>
                    <ComboboxEmpty></ComboboxEmpty>
                    <ComboboxList>
                      {(item) => (
                        <ComboboxItem key={item.id} value={item}>
                          {item.name}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </Field>
            </FieldGroup>
            <FieldGroup className="mb-4">
              <FieldLabel>{t("signup.whatsapp")}</FieldLabel>
              <div className="flex flex-row justify-between gap-2">
                <Field className="w-1/3! min-w-0!">
                  <Combobox
                    items={countryCodes}
                    onValueChange={setPhoneCode}
                    itemToStringLabel={(item) => `${item?.name}`}
                  >
                    <ComboboxInput
                      className="h-10 w-full!"
                      placeholder={t("signup.code")}
                    >
                      <InputGroupAddon className="text-2xl">
                        {phoneCode?.flag}
                      </InputGroupAddon>
                    </ComboboxInput>
                    <ComboboxContent className="w-full!">
                      <ComboboxEmpty></ComboboxEmpty>
                      <ComboboxList>
                        {(item) => (
                          <ComboboxItem key={item.id} value={item}>
                            <span className="text-2xl">{item.flag}</span>{" "}
                            {item.name}
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                </Field>
                <Field className="w-2/3">
                  <Input
                    id="phone"
                    type="text"
                    placeholder={t("signup.phone")}
                    required
                    className="h-10"
                    value={phone}
                    onChange={onChangePhone}
                    onBlur={validatePhone}
                    onKeyDown={handleNumberKeyDown}
                    onPaste={handleNumberPaste}
                  />
                  {phoneError && (
                    <FieldError className="text-start">
                      {t("signup.phone")} {t("signup.required")}
                    </FieldError>
                  )}
                </Field>
              </div>
            </FieldGroup>
            <FieldGroup className="mb-4 flex flex-row justify-between">
              <Field className="w-1/2">
                <FieldLabel>{t("create.loc")}</FieldLabel>
                <Combobox
                  items={countries}
                  onValueChange={setCountry}
                  itemToStringLabel={(item) => item?.name}
                >
                  <ComboboxInput
                    className="h-10"
                    placeholder="Select a Country"
                  />
                  <ComboboxContent>
                    <ComboboxEmpty></ComboboxEmpty>
                    <ComboboxList>
                      {(item) => (
                        <ComboboxItem key={item.id} value={item}>
                          {item.name}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </Field>
              <Field className="w-1/2">
                <FieldLabel>{t("signup.city")}</FieldLabel>
                <Input
                  id="city"
                  type="text"
                  placeholder="City"
                  required
                  className="h-10"
                  value={city}
                  onChange={onChangeCity}
                  onBlur={validateCity}
                />
                {cityError && (
                  <FieldError className="text-start">
                    {t("signup.city")} {t("signup.required")}
                  </FieldError>
                )}
              </Field>
            </FieldGroup>
            <FieldGroup className="w-full">
              <Field className="flex- flex w-full flex-row justify-center">
                <Button
                  type="button"
                  onClick={onClickBackStep2}
                  size="lg"
                  variant="outline"
                  className="w-1/3!"
                >
                  {t("common.back")}
                </Button>
                <Button
                  type="submit"
                  disabled={validateSecondStep()}
                  onClick={onClickContinueStep2}
                  size="lg"
                  className="w-2/3!"
                >
                  {t("signup.continue")}
                </Button>
              </Field>
            </FieldGroup>
          </form>
        </div>
      )}
      {step === 3 && (
        <div className="flex w-full max-w-md flex-col items-center">
          <div className="mb-4 flex w-full max-w-md items-center justify-between gap-2 pe-6">
            <Badge>
              <CheckIcon />
            </Badge>
            <Separator className="w-1/3!" />
            <Badge>
              <CheckIcon />
            </Badge>
            <Separator className="w-1/3!" />
            <Badge>3</Badge>
          </div>
          <div className="flex w-full max-w-md">
            <h2
              className={cn(
                "mb-4 text-center font-heading text-xl",
                locale === "ar" && "font-arabic!"
              )}
            >
              {t("signup.title3")}
            </h2>
          </div>
          <form className="w-full max-w-md">
            {isError && completeError && (
              <div className="mt-2 mb-4 rounded-sm bg-background p-2">
                <FieldError className="mb-2">{completeError}</FieldError>
                <Link
                  to={"/login"}
                  className="ms-auto inline-block text-sm underline-offset-4 hover:underline"
                >
                  {t("signup.signin")}
                </Link>
                <span> | </span>
                <Link
                  to={"/account/forget-password"}
                  className="ms-auto inline-block text-sm underline-offset-4 hover:underline"
                >
                  {t("login.forget")}
                </Link>
              </div>
            )}
            <FieldGroup className="mb-4 w-full">
              <Field>
                <FieldLabel htmlFor="formats">
                  {t("signup.formatsTitle")}
                </FieldLabel>
                <Combobox
                  items={formats || []}
                  multiple
                  value={pitchFormat}
                  onValueChange={onChangePitchFormats}
                  id="formats"
                >
                  <ComboboxChips>
                    <ComboboxValue>
                      {pitchFormat.map((item) => (
                        <ComboboxChip key={item.name}>{item.name}</ComboboxChip>
                      ))}
                    </ComboboxValue>
                    <ComboboxChipsInput
                      placeholder={`${t("create.select")}${t("create.format")}`}
                    />
                  </ComboboxChips>
                  <ComboboxContent>
                    <ComboboxEmpty></ComboboxEmpty>
                    <ComboboxList>
                      {(item) => (
                        <ComboboxItem
                          key={item.name}
                          value={{ id: item.id, name: item.name }}
                        >
                          {item.name}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </Field>
            </FieldGroup>
            <FieldGroup className="mb-4 w-full">
              <Field>
                <FieldLabel htmlFor="topics">
                  {t("signup.topicsTitle")}
                </FieldLabel>
                <Combobox
                  items={themes || []}
                  multiple
                  value={pitchThemes}
                  onValueChange={onChangePitchThemes}
                  id="formats"
                >
                  <ComboboxChips>
                    <ComboboxValue>
                      {pitchThemes.map((item) => (
                        <ComboboxChip key={item.name}>{item.name}</ComboboxChip>
                      ))}
                    </ComboboxValue>
                    <ComboboxChipsInput
                      placeholder={`${t("create.select")}${t("create.themes")}`}
                    />
                  </ComboboxChips>
                  <ComboboxContent>
                    <ComboboxEmpty></ComboboxEmpty>
                    <ComboboxList>
                      {(item) => (
                        <ComboboxItem
                          key={item.name}
                          value={{ id: item.id, name: item.name }}
                        >
                          {item.name}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </Field>
            </FieldGroup>
            <FieldGroup className="w-full">
              <Field className="flex w-full flex-row items-center justify-center">
                <Button
                  type="button"
                  onClick={onClickBackStep3}
                  size="lg"
                  variant="outline"
                  className="w-1/3!"
                >
                  {t("common.back")}
                </Button>
                <Button
                  type="submit"
                  disabled={validateThirdStep() || isSubmitting}
                  onClick={onClickComplete}
                  size="lg"
                  className="w-2/3!"
                >
                  {isSubmitting ? t("signup.submitting") : t("signup.complete")}
                </Button>
              </Field>
            </FieldGroup>
          </form>
        </div>
      )}
      {step === 4 && (
        <div className="m-8 flex flex-col items-center gap-4 text-center font-heading text-lg">
          <CheckCircle size="48" />
          {t("signup.success")}
          <span className="font-sans text-sm">{t("signup.verify")}</span>
        </div>
      )}
      <div>
        <LanguageSelector />
      </div>
    </div>
  )
}
